/**
 * Computes default/initial values for the Details step form.
 * Keys MUST match the field `name` in useDetailsFormSchema.
 */
import { useMemo } from 'react';
import { useLocationFieldConfig } from '@/formConfigs';
import type { ScheduleMode, ApiReference } from '../../schedule.types';

export interface DetailsFormValues {
  [key: string]: unknown;
  interviewName: string;
  interview_type: string;
  participant_ids: string | string[];
  book_meeting_room: boolean;
  meeting_room_ids: string[];
  calendar_info: string;
  duration: number;
  dateTime: string;
  startDate: Date | null;
  startTime: string;
  endTime: string;
  isDateTimeEditing: string;
  findTime: string;
  calendar_visibility: boolean;
  location: string;
  host_id: string;
  ats_office_id: string;
  candidatePhone: string;
  note_taker_enabled: boolean;
  fill_with_ai_enabled: boolean;
}

export function useDetailsFormDefaults(
  interviewPlan: Record<string, unknown> | undefined,
  mode: ScheduleMode,
  apiReference: ApiReference
): DetailsFormValues {
  const isEdit = apiReference === 'edit';
  const isAdhoc = mode === 'adhoc' || mode === 'self-schedule-adhoc';
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';

  const { defaultLocation: availableDefault } = useLocationFieldConfig({});

  return useMemo(() => {
    const plan = interviewPlan ?? {};

    const interviewName =
      isAdhoc && !isEdit
        ? ''
        : ((plan.substituted_name as string) ?? (plan.name as string) ?? '');

    const interview_type =
      (plan.kind as string) ?? (plan.interview_kind as string) ?? 'one_on_one';

    const participants = Array.isArray(plan.participants)
      ? (plan.participants as Array<{ id?: number }>)
      : [];
    const interviewerIds = participants
      .map((p) => String(p.id))
      .filter(Boolean);
    const participant_ids = isSelfSchedule
      ? interviewerIds
      : (interviewerIds[0] ?? '');

    const duration =
      typeof plan.duration === 'number' && plan.duration > 0
        ? plan.duration
        : 30;

    // Smart location fallback: plan value > first available from account settings
    const planLocation = plan.location as string | undefined;
    const location =
      planLocation && planLocation !== 'unspecified'
        ? planLocation
        : (availableDefault ?? '');

    const hostObj =
      typeof plan.host === 'object' && plan.host !== null
        ? (plan.host as Record<string, unknown>)
        : null;
    const host_id = hostObj?.id ? String(hostObj.id) : '';

    const meetingRooms = Array.isArray(plan.meeting_rooms)
      ? (plan.meeting_rooms as Array<{ email?: string }>)
          .map((r) => r.email ?? '')
          .filter(Boolean)
      : [];
    const book_meeting_room = meetingRooms.length > 0;

    const calendar_visibility =
      plan.calendar_visibility === 'private' ||
      plan.calendar_visibility === true;
    const note_taker_enabled = Boolean(plan.note_taker_enabled);
    const fill_with_ai_enabled = Boolean(plan.fill_with_ai_enabled ?? true);

    return {
      interviewName,
      interview_type,
      participant_ids,
      book_meeting_room,
      meeting_room_ids: meetingRooms,
      calendar_info: '',
      duration,
      dateTime: '',
      startDate: null,
      startTime: '',
      endTime: '',
      isDateTimeEditing: 'view',
      findTime: '',
      calendar_visibility,
      location,
      host_id,
      ats_office_id: '',
      candidatePhone: '',
      note_taker_enabled,
      fill_with_ai_enabled,
    };
  }, [interviewPlan, isEdit, isAdhoc, isSelfSchedule, availableDefault]);
}
